import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { isUndefined, cloneDeep, isEqual } from 'lodash';
import { Subject, BehaviorSubject } from 'rxjs';

class BaseCallMaker {
    /**
     *
     * @param promiseCreator Method for actually fetching the data. Cannot return `undefined`
     * @param softEquals When comparing the arguments, should we use a `===`(soft) or a lodash equals(!soft)
     * @param debounceTime How long to wait between calls
     * @param optimisticFirstCall Make call immediately on first invocation
     * @param doArgCheck Compare args between calls
     */
    constructor(promiseCreator, softEquals = false, debounceTime = 1000, optimisticFirstCall = false, doArgCheck = true) {
        this.promiseCreator = promiseCreator;
        this.softEquals = softEquals;
        this.debounceTime = debounceTime;
        this.optimisticFirstCall = optimisticFirstCall;
        this.doArgCheck = doArgCheck;
        this.count = 0;
        this._result = new Subject();
        this.result = this._result;
        this.loading$ = new BehaviorSubject(false);
    }
    /**
     * Complete all subscriptions
     */
    complete() {
        this._result.complete();
        this.loading$.complete();
    }
    /**
     * Call `promiseCreator` from constructor
     *
     * @param arg Method used to obtain the arg for the call
     */
    async invoke(arg, force = false) {
        if (isUndefined(this.lastResult) && this.invocation) {
            await this.invocation;
        }
        const count = ++this.count;
        return this.invocation = new Promise(async (res, reject) => {
            const debounceTime = (this.optimisticFirstCall && isUndefined(this.lastResult)) ?
                0 :
                this.debounceTime;
            if (debounceTime) {
                setTimeout(async () => {
                    try {
                        const result = await this.doInvoke(count, debounceTime, arg, force);
                        res(result);
                        this.invocation = null;
                    }
                    catch (e) {
                        console.error(e);
                        this.loading = false;
                        this.loading$.next(false);
                        reject();
                    }
                }, debounceTime);
            }
            else {
                try {
                    const result = await this.doInvoke(count, debounceTime, arg, force);
                    res(result);
                    this.invocation = null;
                }
                catch (e) {
                    console.error(e);
                    this.loading = false;
                    this.loading$.next(false);
                    reject();
                }
            }
        });
    }
    async doInvoke(count, debounceTime, arg, force = false) {
        let result = this.lastResult;
        if ((count === this.count) || (debounceTime === 0)) {
            const argument = arg();
            if (this.loading) {
                this.nextCall = this.invoke.bind(this, arg);
            }
            else if (!this.compare(argument, this.lastArg) || force) {
                this.loading = true;
                this.loading$.next(true);
                this.lastArg = this.softEquals ? argument : cloneDeep(argument);
                result = await this.promiseCreator(argument);
                this.lastResult = result;
                this.loading = false;
                this.loading$.next(false);
                if (this.nextCall) {
                    result = await this.nextCall();
                }
                this._result.next(result);
                this.currentResult = result;
            }
        }
        this.lastResult = result;
        return result;
    }
    compare(val1, val2) {
        if (!this.doArgCheck) {
            return false;
        }
        return this.softEquals ?
            val1 === val2 :
            isEqual(val1, val2);
    }
    /**
     * Reset arguments to cause the next call to always trigger
     */
    reset() {
        this.lastArg = null;
        this.nextCall = null;
    }
    /**
     * Reset the call maker and clear out existing results
     */
    reinit() {
        this.reset();
        this.lastResult = undefined;
    }
}
class CallMaker extends BaseCallMaker {
}
class CallMakerFactory {
    /**
     *
     * @param func Method for actually fetching the data. Cannot return `undefined`
     * @param softEquals When comparing the arguments, should we use a `===`(soft) or a lodash equals(!soft)
     * @param debounceTime How long to wait between calls
     * @param optimisticFirstCall Make call immediately on first invocation
     * @param doArgCheck Compare args between calls
     */
    static create(func, debounceTime = 500, softEquals = false, optimisticFirstCall = false, doArgCheck = true) {
        const maker = new BaseCallMaker(func, softEquals, debounceTime, optimisticFirstCall, doArgCheck);
        return maker;
    }
    /**
     *
     * @param func Method for actually fetching the data. Cannot return `undefined`
     * @param softEquals When comparing the arguments, should we use a `===`(soft) or a lodash equals(!soft)
     * @param debounceTime How long to wait between calls
     * @param optimisticFirstCall Make call immediately on first invocation
     * @param doArgCheck Compare args between calls
     */
    create(func, debounceTime = 500, softEquals = false, optimisticFirstCall = false, doArgCheck = true) {
        return CallMakerFactory.create(func, debounceTime, softEquals, optimisticFirstCall, doArgCheck);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerFactory, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerFactory, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerFactory, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class CallMakerModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "19.2.10", ngImport: i0, type: CallMakerModule }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "19.2.10", ngImport: i0, type: CallMakerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    declarations: [],
                    exports: [],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CallMaker, CallMakerFactory, CallMakerModule };
